import React, { useCallback, useEffect, useState } from "react";

import ArrowForward from "@mui/icons-material/ArrowForward";
import { SubscriptionPlans } from "@prisma/client";
import { useSession } from "next-auth/react";
import { useDropzone } from "react-dropzone";
import { v4 as uuid } from "uuid";

import Button from "@musicfy/components/Button";
import { MAX_FILE_SIZE } from "@musicfy/contants/Features";
import { useFFmpegContext } from "@musicfy/libs/FFmpegProvider";
import { useRequireAuthContext } from "@musicfy/libs/RequireAuthProvider";
import { useSubscriptionContext } from "@musicfy/libs/SubscriptionProvider";
import { useToastContext } from "@musicfy/libs/ToastProvider";
import { cleanFilename } from "@musicfy/utils";
import { normalizeAudioContentType } from "@musicfy/utils/audioUpload";
import useStorage, {
  Buckets,
  type IFileUpload,
} from "@musicfy/utils/hooks/useStorage";

import AudioRecorder from "./AudioRecorder";
import InferenceFileUploader from "./InferenceFileUploader";
import UploadedInferenceFilesList from "./UploadedInferenceFilesList";
// import YoutubeToMp3 from "./YoutubeToMp3";
import { type OnboardingAction, type OnboardingState } from "../types";

const MultiFileUploader: React.FC<{
  uploadedFiles: IFileUpload[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<IFileUpload[]>>;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  dispatchOnboarding: React.Dispatch<OnboardingAction>;
  onboardingState: OnboardingState;
}> = ({
  uploadedFiles,
  setUploadedFiles,
  setIsUploading,
  dispatchOnboarding,
  onboardingState,
}) => {
  const { uploadFiles, isUploadingFiles, uploadProgress } = useStorage();
  const { subscription } = useSubscriptionContext();
  const { sendToastMessage } = useToastContext();
  const { ffmpeg, fetchFile, load } = useFFmpegContext();

  const [displayUploadedFiles, setDisplayUploadedFiles] =
    useState<boolean>(false);

  const { data } = useSession();
  const { showAuthModal } = useRequireAuthContext();
  const user = data?.user;

  const maxFileSize =
    MAX_FILE_SIZE[subscription?.plan || SubscriptionPlans.free];
  const maxFileSizeInMB = maxFileSize / 1024 / 1024;

  const uploadInferenceFiles = useCallback(
    async (files: File[]) => {
      if (!user) return;

      load();

      let isCorrupted = false;
      for (const file of files) {
        if (!fetchFile || !ffmpeg) break;

        try {
          await ffmpeg?.writeFile(file.name, await fetchFile(file));
          const execResult = await ffmpeg?.exec([
            "-i",
            file.name,
            "-vn",
            "-acodec",
            "pcm_s16le",
            "-ac",
            "2",
            "-ar",
            "44100",
            "output.wav",
            "-y",
          ]);
          if (execResult === 1) {
            isCorrupted = true;
            break;
          }
        } catch {
          isCorrupted = false;
          break;
        }
      }

      if (isCorrupted) {
        sendToastMessage(`File is corrupted, try a different file`, {
          severity: "error",
        });
        return;
      }

      // Generate array of files to upload
      const filesToUpload = files.map((file) => {
        // 1. Replace spaces, apostrophes, dashes, and parentheses with underscores
        const filename = cleanFilename(file.name);
        const contentType = normalizeAudioContentType(file.type, filename);

        // 2. Save the file with the new filename and normalized MIME type
        const newFile = new File([file], filename, {
          type: contentType,
        });

        // 3. Generate a unique ID and upload the file
        const audioId = uuid();
        const path = `${user.id}/${audioId}/${newFile.name}`;

        return {
          audioId: audioId,
          file: newFile,
          bucket: Buckets.AUDIO_CLIPS,
          filename: newFile.name,
          path: path,
        };
      });

      // Ensure that files with the same name are uploaded with unique names
      filesToUpload.forEach((fileToUpload) => {
        const fileIndex = uploadedFiles.findIndex(
          (audioClip) => fileToUpload.file.name === audioClip.file.name
        );

        if (fileIndex !== -1) {
          const filename = fileToUpload.path.split("/").pop() || "";

          const extension = filename?.split(".").pop() || ".wav";
          const filenameWithoutExtension =
            filename?.split(".").slice(0, -1).join(".") || "";

          const numberOfFilesWithSameName = uploadedFiles.filter(
            (uploadedFile) =>
              uploadedFile.path.includes(`${filenameWithoutExtension}`)
          ).length;

          const newFilename = `${filenameWithoutExtension}_${numberOfFilesWithSameName}.${extension}`;

          fileToUpload.path = fileToUpload.path.replace(filename, newFilename);
          fileToUpload.filename = newFilename;
        }
      });

      setUploadedFiles((prev) => [...prev, ...filesToUpload]);
      setDisplayUploadedFiles(true);

      try {
        await uploadFiles(filesToUpload);
      } catch (err) {
        setUploadedFiles((prev) =>
          prev.filter(
            (item) => !filesToUpload.some((failed) => failed.path === item.path)
          )
        );
        sendToastMessage("File upload failed. Please try again.", {
          severity: "error",
        });
      }
    },
    [
      fetchFile,
      ffmpeg,
      load,
      sendToastMessage,
      setUploadedFiles,
      uploadFiles,
      uploadedFiles,
      user,
    ]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (!user) {
        showAuthModal();
        return;
      }
      if (!acceptedFiles.length) return;

      const hasAnyLargeFile = acceptedFiles.some(
        (file) => file.size > maxFileSize
      );

      if (hasAnyLargeFile) {
        sendToastMessage(`File must be less than ${maxFileSizeInMB}MB`, {
          severity: "error",
        });
        return;
      }

      uploadInferenceFiles(acceptedFiles);
    },
    [
      user,
      uploadInferenceFiles,
      showAuthModal,
      maxFileSize,
      sendToastMessage,
      maxFileSizeInMB,
    ]
  );

  const deleteInferenceFile = useCallback(
    (path: string) => {
      setUploadedFiles((prev) => {
        return prev.filter((i) => i.path !== path);
      });
    },
    [setUploadedFiles]
  );

  useEffect(() => {
    setIsUploading(isUploadingFiles);
  }, [isUploadingFiles, setIsUploading]);

  useEffect(() => {
    if (
      uploadedFiles.length === 1 &&
      onboardingState.currentStepIndex === 1 &&
      onboardingState.isOpen &&
      !isUploadingFiles
    ) {
      dispatchOnboarding({ type: "NEXT_STEP" });
    }
  }, [
    uploadFiles,
    dispatchOnboarding,
    onboardingState,
    uploadedFiles,
    isUploadingFiles,
  ]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "audio/mpeg": [".mp3"],
      "audio/wav": [".wav"],
    },
  });

  return (
    <div className="relative bg-gradient-to-br from-gray-50/10 to-teal/15 rounded-18 border border-white border-opacity-[.2] flex flex-col p-7">
      <input disabled={isUploadingFiles} {...getInputProps()} />
      {uploadedFiles.length > 0 && !displayUploadedFiles && (
        <div>
          <Button
            onClick={() => {
              setDisplayUploadedFiles(true);
            }}
            variant="text"
            style="tertiary"
            className="mb-4"
            postIcon={<ArrowForward />}
          >
            View Uploaded Files
          </Button>
        </div>
      )}
      {!displayUploadedFiles || uploadedFiles.length === 0 ? (
        <>
          <div className="flex xxl:flex-row flex-col justify-between gap-5">
            <div
              {...getRootProps()}
              className="w-full"
              aria-label="Upload Audio"
            >
              <InferenceFileUploader isDragActive={isDragActive} />
            </div>
            <div className="w-full">
              <AudioRecorder
                onRecordingComplete={(audioFile) => {
                  uploadInferenceFiles([audioFile]);
                }}
              />
            </div>
          </div>

          {/* <div className="mt-6">
            <div
              className="text-white text-base font-medium capitalize"
              aria-label=""
            >
              Or Add a Youtube Link
            </div>
            <div className="w-full mt-4">
              <YoutubeToMp3
                onConverted={(youtubeFile) => {
                  uploadInferenceFiles([youtubeFile]);
                }}
              />
            </div>
          </div> */}
        </>
      ) : (
        <div {...getRootProps()} className="w-full">
          <UploadedInferenceFilesList
            uploadedFiles={uploadedFiles}
            uploadProgress={uploadProgress}
            onDelete={deleteInferenceFile}
            onAddMoreFilesClick={() => {
              setDisplayUploadedFiles(false);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MultiFileUploader;
